import { useEffect, useRef, useState } from 'react';
import { articoli } from '../../api/articoli';
import type { ArticoloTrovato } from '../../api/articoli';
import { formattaImporto } from '../../lib/formato';

/*  Cercare un articolo del catalogo e metterlo nel preventivo.

    È il gesto centrale della compilazione, e finora non c'era affatto: si poteva scrivere
    una voce a mano, e basta. Una riga senza codice articolo non ha un costo che venga da
    un listino, non ha un confronto fra fornitori, e non si aggiorna quando arriva il
    listino nuovo — cioè metà dell'applicativo non la tocca.

    TUTTE LE PAROLE, NON UNA QUALUNQUE

    «tubo rame 1/2» deve trovare i raccordi per tubo di rame da mezzo pollice — sui dati
    veri sono sette articoli su centoottomila. Con l'OR sarebbero tutti i tubi più tutto il
    rame, migliaia di righe, e chi cerca imparerebbe in tre giorni a non usare la ricerca.
    Con l'AND ogni parola stringe, che è come funziona la testa di chi la scrive: si
    aggiunge una parola quando i risultati sono troppi.

    Il più divide come lo spazio, perché «tubo+rame+1/2» è come lo scrive chi cerca. La
    barra no: «1/2» è mezzo pollice, e spezzarlo darebbe due cifre che stanno su mezzo
    catalogo.

    IL COSTO SI VEDE PRIMA DI SCEGLIERE

    Un elenco di descrizioni senza prezzi obbligherebbe a mettere l'articolo nel preventivo
    per sapere quanto costa, e a toglierlo se costa troppo. Il costo migliore e da chi
    viene stanno nella riga del risultato, insieme a quanti lo offrono — «22,58 da un
    fornitore solo» e «22,58 su quattro» sono due situazioni diverse per chi compra.
*/
export function CercaArticolo({
  dentro,
  inCorso,
  onScelto,
}: {
  /** Il capitolo in cui finirà, per nome: «dentro Caldaia murale». Vuoto se va in coda. */
  dentro?: string;
  inCorso: boolean;
  onScelto: (articolo: ArticoloTrovato) => void;
}) {
  const [frase, setFrase] = useState('');
  const [trovati, setTrovati] = useState<ArticoloTrovato[] | null>(null);
  const [colCodiceFornitore, setColCodiceFornitore] = useState(false);
  const [cercando, setCercando] = useState(false);
  const [errore, setErrore] = useState<string | null>(null);

  /*  L'ultima richiesta partita. Senza, due ricerche in volo tornano nell'ordine in cui
      il server risponde e non in quello in cui sono partite: si finisce di scrivere
      «defangatore» e a schermo restano i risultati di «defang», che è il modo più sicuro
      di far scegliere l'articolo sbagliato.  */
  const ultima = useRef(0);

  useEffect(() => {
    const testo = frase.trim();

    /*  Tre lettere, non due. Misurato sui dati veri: «ca» corrisponde a ventitremila
        articoli su centododicimila, e restituirne venticinque presi da quella massa non
        è una risposta a niente — costava due secondi e due decimi per mostrare della
        mobilia da bagno a chi stava scrivendo «caldaia».  */
    if (testo.length < 3) {
      setTrovati(null);
      setCercando(false);
      return;
    }

    /*  Quasi mezzo secondo dopo l'ultimo tasto, e non un quarto. La ricerca costa una
        scansione per parola sul catalogo: a 250ms chi scrive a velocità normale ne lancia
        cinque per arrivare a «caldaia», e le prime quattro sono lavoro buttato che rallenta
        la quinta. Il costo di aspettare è che si vede il risultato un attimo dopo; il costo
        di non aspettare è che lo si vede molto dopo.  */
    setCercando(true);
    const attesa = setTimeout(() => {
      const mia = ++ultima.current;

      articoli
        .cerca(testo)
        .then((risposta) => {
          if (mia !== ultima.current) return;
          setTrovati(risposta.trovati);
          setColCodiceFornitore(risposta.colCodiceFornitore);
          setErrore(null);
        })
        .catch((e: Error) => {
          if (mia !== ultima.current) return;
          setErrore(e.message);
          setTrovati([]);
        })
        .finally(() => {
          if (mia === ultima.current) setCercando(false);
        });
    }, 450);

    return () => clearTimeout(attesa);
  }, [frase]);

  function scegli(articolo: ArticoloTrovato) {
    onScelto(articolo);

    /*  Il campo si svuota e l'elenco sparisce: si mette un articolo per volta, e lasciare
        i risultati a schermo invita a premere due volte lo stesso — che nel preventivo
        diventa la stessa voce due volte, con due costi identici che nessuno nota.  */
    setFrase('');
    setTrovati(null);
  }

  return (
    <div className="cerca-articolo">
      <label>
        Dal catalogo{dentro ? <>, dentro «{dentro}»</> : null}
        <input
          value={frase}
          onChange={(e) => setFrase(e.target.value)}
          placeholder="defangatore magnetico"
          disabled={inCorso}
        />
      </label>

      {errore !== null && <p className="errore">{errore}</p>}

      {cercando && <p className="nota">Cerco…</p>}

      {trovati !== null && trovati.length === 0 && !cercando && (
        <p className="nota">
          Nessun articolo con tutte queste parole, né per descrizione né per codice —
          del produttore o di un fornitore. Ogni parola stringe: toglierne una allarga la
          ricerca, e «tubo+rame+1/2» vale «tubo rame 1/2».
        </p>
      )}

      {/*  Un risultato che non somiglia a quello che si è battuto va spiegato: cercando
           «2430586» esce un lavabo che si chiama «BH0800465011TS0», e senza questa riga
           sembra che la ricerca abbia risposto a caso.  */}
      {colCodiceFornitore && trovati !== null && trovati.length > 0 && (
        <p className="nota">
          Nessuno per descrizione o codice del produttore: questi hanno il codice di un
          fornitore che corrisponde.
        </p>
      )}

      {trovati !== null && trovati.length > 0 && (
        <ul className="trovati">
          {trovati.map((a) => (
            <li key={a.id}>
              <button type="button" disabled={inCorso} onClick={() => scegli(a)}>
                <span className="chiave">
                  {a.siglaProduttore} {a.codiceProduttore}
                </span>
                <span className="descrizione">{a.descrizione}</span>
                <span className="costo">
                  {a.migliorCosto === null ? 'nessuna offerta' : formattaImporto(a.migliorCosto)}
                </span>
                <span className="da">
                  {a.migliorFornitore ?? ''}
                  {a.fornitoriConOfferta !== null && a.fornitoriConOfferta > 1 && (
                    <em> · {a.fornitoriConOfferta} fornitori</em>
                  )}
                </span>
              </button>
            </li>
          ))}
        </ul>
      )}
    </div>
  );
}
